import React, { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  useTheme,
  TextField,
  Stack,
  Button,
  Pagination,
  Chip,
} from '@mui/material';
import { format, parseISO, isToday, isTomorrow, isPast, differenceInDays, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ContextSession } from '../../../services/ContextSession';
import Lottie from 'lottie-react';
import alerta from './alerta.json';
 
const ALERT_STATUS = {
  RED: 'red',
  YELLOW: 'yellow',
  NONE: 'none',
  COMPLETED: 'completed'
};

const PRIORITY_WEIGHTS = {
  [ALERT_STATUS.RED]: 4,
  [ALERT_STATUS.YELLOW]: 3,
  [ALERT_STATUS.NONE]: 2,
  [ALERT_STATUS.COMPLETED]: 1
};

// Función para obtener el texto de días restantes
const getRemainingDaysText = (dateStr) => {
  if (!dateStr) return 'Fecha no definida';

  const date = parseISO(dateStr);
  if (!isValid(date)) return 'Fecha inválida';

  const daysDiff = differenceInDays(date, new Date());

  if (isToday(date)) return 'Hoy';
  if (isTomorrow(date)) return 'Mañana';
  if (isPast(date)) return `Vencido hace ${Math.abs(daysDiff)} días`;
  return `Vence en ${daysDiff} días`;
};

// Función para determinar el estado de alerta
const getAlertStatus = (fechaInicioStr, fechaFinalizacionStr, estado) => {
  if (estado === 'Cerrado' || fechaFinalizacionStr) return ALERT_STATUS.COMPLETED;

  if (!fechaInicioStr || !isValid(parseISO(fechaInicioStr))) return ALERT_STATUS.NONE;

  const fechaInicio = parseISO(fechaInicioStr);
  const diasSinFinalizar = differenceInDays(new Date(), fechaInicio);

  if (diasSinFinalizar > 10) return ALERT_STATUS.RED;
  if (diasSinFinalizar > 5) return ALERT_STATUS.YELLOW;
  return ALERT_STATUS.NONE;
};

const formatDateSafe = (dateStr) => {
  if (!dateStr) return 'N/D';

  const date = parseISO(dateStr);
  return isValid(date) ? format(date, 'PPP', { locale: es }) : 'Fecha inválida';
};

// Función para determinar qué fecha usar como referencia principal
const getFechaReferencia = (tramite) => {
  // Priorizar fecha_comite si existe y es válida
  if (tramite.fecha_comite && isValid(parseISO(tramite.fecha_comite))) {
    return {
      fecha: tramite.fecha_comite,
      tipo: 'comité',
      parsedDate: parseISO(tramite.fecha_comite)
    };
  }
  // Usar fecha_asignacion como fallback
  return {
    fecha: tramite.fecha_asignacion,
    tipo: 'asignación',
    parsedDate: parseISO(tramite.fecha_asignacion)
  };
};

const ProximosTramitesTgu = () => {
  const navigate = useNavigate();
  const [allTramites, setAllTramites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Sesión
    const sessi = useContext(ContextSession);


  // Función para obtener y procesar los trámites
const fetchTramites = useCallback(async () => {
    try {
      setLoading(true);
      
      // 1. Primero obtenemos la lista de procuradores
      const procuradoresResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/auth/oficialesLegal/');
      if (!procuradoresResponse.ok) throw new Error('Error al cargar procuradores');
      const procuradores = await procuradoresResponse.json();

      // 2. Obtenemos el procurador logueado
      const procuradorLogueado = procuradores.find(p => p.id === sessi.sessionData?.id);
      if (!procuradorLogueado) throw new Error('Procurador no encontrado');

      // 3. Obtenemos los trámites de la sucursal
      const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/por-sucursal/?sucursal_id=1');
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();

      // 4. Filtramos por procurador y luego por estado/fechas
      const tramitesFiltrados = data.filter(item => {
        // Filtro por procurador (comparando nombres exactos)
        const esDelProcurador = item.procurador === procuradorLogueado.nombre;
        
        // Filtro por estado y fechas válidas (manteniendo tu lógica original)
        const fechaComiteValida = item.fecha_comite && isValid(parseISO(item.fecha_comite));
        const fechaAsignacionValida = item.fecha_asignacion && isValid(parseISO(item.fecha_asignacion));
        
        return esDelProcurador && 
               item.estado === 'Abierto' && 
               (fechaComiteValida || fechaAsignacionValida);
      });

      // Procesamiento adicional (manteniendo tu lógica original)
      const processedData = tramitesFiltrados.map(item => {
        const fechaRef = getFechaReferencia(item);

        return {
          ...item,
          fechaReferencia: fechaRef.fecha,
          tipoFecha: fechaRef.tipo,
          parsedDate: fechaRef.parsedDate,
          isValidDate: true,
          alertStatus: getAlertStatus(item.fecha_asignacion, item.fecha_finalizacion, item.estado),
          isCompleted: !!item.fecha_finalizacion || item.estado === 'Cerrado',
          isOverdue: isPast(fechaRef.parsedDate) && !item.fecha_finalizacion
        };
      });

      setAllTramites(processedData);
      
      console.log('Trámites filtrados:', {
        procurador: procuradorLogueado.nombre,
        totalTramites: data.length,
        tramitesFiltrados: processedData.length
      });
      
    } catch (err) {
      console.error('Error al cargar los trámites:', err);
      setError('Error al cargar los trámites. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [sessi.sessionData?.id]); // Añade la dependencia del ID de sesión

  // Filtrado y ordenamiento de trámites
  const filteredTramites = useMemo(() => {
    if (!allTramites.length) return [];

    let result = [...allTramites]; // Ya están filtrados por fecha válida y estado

    // Aplicar filtros de fecha adicionales
    if (startDate) {
      result = result.filter(item => item.parsedDate >= startDate);
    }

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      result = result.filter(item => item.parsedDate <= endOfDay);
    }

    // Ordenar por prioridad
    return result.sort((a, b) => {
      // Primero por estado de alerta (rojo > amarillo > sin alerta > completados)
      const priorityDiff = PRIORITY_WEIGHTS[b.alertStatus] - PRIORITY_WEIGHTS[a.alertStatus];
      if (priorityDiff !== 0) return priorityDiff;

      // Luego por vencimiento (vencidos primero)
      if (a.isOverdue !== b.isOverdue) {
        return a.isOverdue ? -1 : 1;
      }

      // Finalmente por fecha (más antiguos primero)
      return a.parsedDate - b.parsedDate;
    });
  }, [allTramites, startDate, endDate]);

  // Calcular los trámites para la página actual
  const paginatedTramites = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredTramites.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredTramites, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredTramites.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchTramites();
  }, [fetchTramites]);

 const handleVerDetalles = useCallback((tramiteId) => {
        // Guardar el ID en la sesión para usos posteriores
        // Nota: usa la misma convención de nombres
        sessi.setVariable("tramiteID", tramiteId);
        // Navegar a la ruta anidada bajo Legal
        navigate(`/Legal/detallestgu/${tramiteId}`);
    }, [navigate, sessi]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box 
       mb={1}
        p={2}
        borderRadius={2}
        sx={{ mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Trámites Pendientes
        </Typography>
        <Box 
            borderRadius={2}
            sx={{ width: 'fit-content', mx: 'auto', textAlign: 'center' }}>
           <Lottie
              animationData={alerta}
              loop
              autoplay
              style={{ width: 250, height: 190 }}  
              />
        </Box>
         <Box 
            borderRadius={2}
            sx={{ width: 'fit-content',  justifyContent: 'space-between' , textAlign: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 1160, mx: 'auto' }}>
                <Box mb={1} p={2} borderRadius={2} sx={{ width: '100%' }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ 
                            width: '100%', 
                            flexWrap: 'wrap', 
                            rowGap: 1,
                            justifyContent: 'space-between' 
                        }} 
                        >
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            flexWrap: 'wrap',
                            alignItems: 'center'
                        }}>
                            <DatePicker
                            label="Fecha inicio"
                            value={startDate}
                            onChange={setStartDate}
                            renderInput={(params) => <TextField {...params} size="small" />}
                            maxDate={endDate || undefined}
                            />

                            <DatePicker
                            label="Fecha fin"
                            value={endDate}
                            onChange={setEndDate}
                            renderInput={(params) => <TextField {...params} size="small" />}
                            minDate={startDate || undefined}
                            />

                            <Button
                            variant="outlined"
                            onClick={handleResetFilters}
                            color="error"
                            sx={{ height: 40 }}
                            >
                            Limpiar
                            </Button>
                        </Box>
                        </Stack>
                </Box>
                </Box>
        </Box>

        {filteredTramites.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography variant="body1" color="text.secondary">
              No hay trámites en el rango seleccionado.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {paginatedTramites.map((tramite) => (
                <Grid item xs={12} sm={6} md={4} key={tramite.id}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s',
                      borderLeft: tramite.alertStatus === ALERT_STATUS.RED ? '4px solid #f44336' :
                        tramite.alertStatus === ALERT_STATUS.YELLOW ? '4px solid #ffeb3b' : 'none',
                      '&:hover': {
                        transform: 'translateY(-4px)'
                      }
                    }}
                    onClick={() => handleVerDetalles(tramite.id)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1">
                          Trámite #{tramite.id}
                        </Typography>

                        {tramite.alertStatus === ALERT_STATUS.RED && (
                          <Chip label="¡Urgente!" color="error" size="small" />
                        )}
                        {tramite.alertStatus === ALERT_STATUS.YELLOW && (
                          <Chip label="Atención" color="warning" size="small" />
                        )}
                        {tramite.isCompleted && (
                          <Chip label="Completado" color="success" size="small" />
                        )}
                      </Box>

                      <Box mb={1.5}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Asignado a:</strong> {tramite.procurador || 'No asignado'}
                        </Typography>
                        {tramite.fecha_asignacion && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Fecha asignación:</strong> {formatDateSafe(tramite.fecha_asignacion)}
                          </Typography>
                        )}
                        {tramite.fecha_comite && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Fecha comité:</strong> {formatDateSafe(tramite.fecha_comite)}
                          </Typography>
                        )}
                        {tramite.fecha_finalizacion && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Fecha finalización:</strong> {formatDateSafe(tramite.fecha_finalizacion)}
                          </Typography>
                        )}
                      </Box>

                      <Box
                        mt="auto"
                        pt={1}
                        borderTop={`1px solid ${theme.palette.divider}`}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: tramite.isOverdue
                              ? theme.palette.error.main
                              : theme.palette.success.main
                          }}
                        >
                          {getRemainingDaysText(tramite.fechaReferencia)}
                          {tramite.tipoFecha === 'comité' && ' '}
                          {tramite.tipoFecha === 'asignación' && ' '}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ProximosTramitesTgu;